import React, { useContext, useEffect, useState } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { format, isValid, parseISO } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { getPaymentInfo } from "../../../../data/paymentsAPI";
import { ThemeContext } from "../../../../contexts/ThemeContext";
import { AuthContext } from "../../../../contexts/AuthContext";
import { usePnmIntegrationConfig } from "../../../../shared/hooks/usePnmIntegrationConfig";
import { useTotalAmountDueConfig } from "../../../../shared/hooks/useTotalAmountDueConfig";
import FormItem from "../../../../shared/components/form/FormItem";
import { FlexContainer } from "../../../../shared/components/styled/Container.styled";
import { SectionTitle } from "../../../../shared/components/styled/Typography.styled";
import {
  PAY_NEAR_ME_TITLE,
  PNM_PAYMENT_INFO_UNAVAILABLE_MESSAGE,
} from "../../../../shared/constants/Payments.constants";
import { NOT_APPLICABLE } from "../../../../shared/constants/Defaults.constants";
import { formatCurrency } from "../../../../shared/utils/currency";

const formatDueDate = (dateValue, userTimeZone) => {
  if (!dateValue) {
    return NOT_APPLICABLE;
  }

  const parsed =
    dateValue instanceof Date
      ? dateValue
      : utcToZonedTime(parseISO(dateValue), userTimeZone);

  if (!isValid(parsed)) {
    return NOT_APPLICABLE;
  }

  return format(parsed, "MMMM dd, yyyy");
};

const formatFeeValue = (value) => {
  if (value === 0 || value === "0") {
    return "0";
  }
  return formatCurrency(value);
};

const fieldContainerSx = (isMobile) => ({
  width: "100%",
  "& > div": {
    width: {
      xs: "100%",
      md: "unset",
    },
    "& > div": {
      width: {
        xs: "100%",
        md: "unset",
      },
    },
    "& > div:nth-of-type(2)": {
      width: {
        xs: "100%",
        md: "200px",
      },
    },
  },
  "& .MuiTypography-root": {
    fontSize: isMobile ? "14px" : "inherit",
  },
  "& .MuiInput-root": {
    fontSize: isMobile ? "18px" : "inherit",
    fontWeight: isMobile ? "bold" : "normal",
  },
});

export default function PayNearMePayment({
  token,
  loanId,
  impersonatedUser,
  onLoadErrorChange = () => {},
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { addPendingRequest, removePendingRequest } = useContext(ThemeContext);
  const { activeLoanDetails } = useContext(AuthContext);
  const { showTotalAmountDue } = useTotalAmountDueConfig();
  const { pnmIntegrationEnabled } = usePnmIntegrationConfig();

  // US-986802. Only a client running the payment integration who has also asked for the
  // total sees the total here; either answer on its own leaves this screen as it is.
  const displayTotalAmountDue = showTotalAmountDue && pnmIntegrationEnabled;
  // The figure the dashboard shows, read from the same loan details object so the two
  // screens cannot disagree. The prevalidation call carries only the single next payment
  // due, which is what the borrower would otherwise be asked to pay against.
  const totalAmountDue = activeLoanDetails?.loanDetails?.totalAmountDue;

  const [amountDue, setAmountDue] = useState(0);
  const [lateFees, setLateFees] = useState(0);
  const [fees, setFees] = useState(0);
  const [actualDueDate, setActualDueDate] = useState("");
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const loadPaymentInfo = async () => {
      if (!token || !loanId || loanId === "no-loan") {
        return;
      }

      addPendingRequest();
      try {
        const paymentInfo = await getPaymentInfo(token, loanId, impersonatedUser);

        if (!paymentInfo || paymentInfo.message) {
          setLoadError(true);
          return;
        }

        setAmountDue(paymentInfo.amountDue ?? 0);
        setLateFees(paymentInfo.lateFees ?? 0);
        setFees(paymentInfo.fees ?? 0);
        setActualDueDate(paymentInfo.postingDate || paymentInfo.dueDate || "");
        setLoadError(false);
      } catch (error) {
        console.error(error);
        setLoadError(true);
      } finally {
        removePendingRequest();
      }
    };

    loadPaymentInfo();
    // addPendingRequest/removePendingRequest intentionally omitted — including them
    // retriggers this effect when the loading spinner updates ThemeContext.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impersonatedUser, loanId, token]);

  useEffect(() => {
    onLoadErrorChange(loadError);
  }, [loadError, onLoadErrorChange]);

  const formattedDueDate = formatDueDate(actualDueDate, userTimeZone);

  return (
    <Box width="100%">
      <FlexContainer
        width="100%"
        flexdirection="column"
        gap="0"
        padding="0"
        alignitems="flex-start"
      >
        <SectionTitle
          variant="h2"
          color="primary"
          sx={{
            alignSelf: "flex-start",
            width: "100%",
            paddingBottom: 0,
            marginBottom: "48px",
          }}
        >
          {PAY_NEAR_ME_TITLE}
        </SectionTitle>

        {loadError ? (
          <Typography color="error.main" width="100%">
            {PNM_PAYMENT_INFO_UNAVAILABLE_MESSAGE}
          </Typography>
        ) : (
          <FlexContainer
            padding="25px 0"
            flexdirection="column"
            gap="10px"
            width={isMobile ? "100%" : "unset"}
            sx={fieldContainerSx(isMobile)}
          >
            <FormItem
              elementWidth={isMobile ? "100%" : "200px"}
              label="Actual Due Date"
              inputType="input"
              value={formattedDueDate}
              setValue={() => {}}
              disabled
              inputProps={{
                readOnly: true,
                style: { textAlign: "end" },
              }}
            />
            <FormItem
              elementWidth={isMobile ? "100%" : "200px"}
              label={
                displayTotalAmountDue
                  ? `Total Amount Due (${formatCurrency(totalAmountDue)})`
                  : `Amount Due (${formatCurrency(amountDue)})`
              }
              inputType="input"
              value={formatCurrency(
                displayTotalAmountDue ? totalAmountDue : amountDue,
              )}
              setValue={() => {}}
              disabled
              inputProps={{
                readOnly: true,
                style: { textAlign: "end" },
              }}
            />
            {/* Late charges and fees are already inside the total, so listing them again
                would put the same money in front of the borrower twice. */}
            {!displayTotalAmountDue && (
              <>
                <FormItem
                  elementWidth={isMobile ? "100%" : "200px"}
                  label={`Late Fees (${formatCurrency(lateFees)})`}
                  inputType="input"
                  value={formatFeeValue(lateFees)}
                  setValue={() => {}}
                  disabled
                  inputProps={{
                    readOnly: true,
                    style: { textAlign: "end" },
                  }}
                />
                <FormItem
                  elementWidth={isMobile ? "100%" : "200px"}
                  label={`Fees (${formatCurrency(fees)})`}
                  inputType="input"
                  value={formatFeeValue(fees)}
                  setValue={() => {}}
                  disabled
                  inputProps={{
                    readOnly: true,
                    style: { textAlign: "end" },
                  }}
                />
              </>
            )}
          </FlexContainer>
        )}
      </FlexContainer>
    </Box>
  );
}
